import { createContext, useState, useContext, useCallback } from 'react';

import { estimatedTripFare } from 'api/taxiTrip';

import { EstimatedFareItem } from 'api/taxiTrip/model/FEModel';

const defaultEstimatedFare: EstimatedFareItem = {
  productId: '',
  displayName: '',
  fareId: '',
  fare: 0,
  expiresUtc: '',
  cancelThresholdSecs: 0,
  minCalcelFee: 0,
  description: '',
  pictureUrl: '',
  capacity: 0,
  pickupEstimate: 0,
  fare_max: 0,
  fare_min: 0,
};

interface UpdateEstimatedFareParams {
  canClearFare?: boolean;
  canUpdateFare?: boolean;
  updatedPlace: {
    originPlace: string;
    originPlaceLat: number;
    originPlaceLng: number;
    originPlaceTag: string;
    destinationPlace: string;
    destinationPlaceLat: number;
    destinationPlaceLng: number;
    destinationPlaceTag: string;
  };
  orderDetail: {
    uberOptions: { id: string; isChecked: boolean }[];
  };
}

interface EstimatedFareContextType {
  carTypeFareList: EstimatedFareItem[];
  estimatedFare: EstimatedFareItem;
  setEstimatedFare: (fare: EstimatedFareItem) => void;
  resetEstimatedFare: () => void;
  updateEstimatedFare: (
    params: UpdateEstimatedFareParams
  ) => Promise<{ res: EstimatedFareItem | null; list: EstimatedFareItem[]; error: any }>;
  isEstimatedFareLoading: boolean;
}

const EstimatedFareContext = createContext<EstimatedFareContextType | null>(null);

export function useEstimatedFare() {
  const context = useContext(EstimatedFareContext);
  if (!context) {
    throw new Error('useEstimatedFare must be used within an EstimatedFareProvider');
  }
  return context;
}

export function EstimatedFareProvider({ children }) {
  const [estimatedFare, setEstimatedFare] = useState<EstimatedFareItem>(defaultEstimatedFare);
  const [carTypeFareList, setCarTypeFareList] = useState<EstimatedFareItem[]>([]);
  const [isEstimatedFareLoading, setIsEstimatedFareLoading] = useState(false);

  const resetEstimatedFare = useCallback(() => {
    setEstimatedFare(defaultEstimatedFare);
    setCarTypeFareList([]);
    setIsEstimatedFareLoading(false);
  }, [setEstimatedFare]);

  const updateEstimatedFare = useCallback(
    async ({
      canClearFare = true,
      canUpdateFare = true,
      updatedPlace,
      orderDetail,
    }: UpdateEstimatedFareParams) => {
      setIsEstimatedFareLoading(true);
      const {
        originPlace,
        originPlaceLat,
        originPlaceLng,
        originPlaceTag,
        destinationPlace,
        destinationPlaceLat,
        destinationPlaceLng,
        destinationPlaceTag,
      } = updatedPlace;

      const hasAllCoordinates = [
        originPlaceLat,
        originPlaceLng,
        destinationPlaceLat,
        destinationPlaceLng,
      ].every((coord) => coord != null && coord !== 0);

      if (!hasAllCoordinates) {
        resetEstimatedFare();
        setIsEstimatedFareLoading(false);
        return { res: null, list: [], error: null };
      }

      const PLACE_TAG_PREDICT = 'predict';

      const getOptionChecked = (options) => (elementId) =>
        options.find((option) => option.id === elementId)?.isChecked ?? false;

      const getOptions = getOptionChecked(orderDetail.uberOptions);

      const postData = {
        pickupLat: originPlaceLat,
        pickupLon: originPlaceLng,
        dropoffLat: destinationPlaceLat,
        dropoffLon: destinationPlaceLng,
        option: {
          baggage: getOptions('input1'),
          pet: getOptions('input3'),
        },
        pickupAddress: originPlace,
        checkPickupAdd: originPlaceTag !== PLACE_TAG_PREDICT,
        dropoffAddress: destinationPlace,
        checkDropoffAdd: destinationPlaceTag !== PLACE_TAG_PREDICT,
      };

      const resetFareIfNeeded = () => {
        if (canClearFare && window.history.state !== 'tab') {
          resetEstimatedFare();
        }
      };

      const [result, error] = await estimatedTripFare(postData);

      if (error) {
        resetFareIfNeeded();
        setIsEstimatedFareLoading(false);
        return { res: null, list: [], error };
      }

      const updatedEstimatedFare: EstimatedFareItem | undefined = result?.data?.data?.[0];
      const carTypeFareList: EstimatedFareItem[] = result?.data?.data ?? [];
      setCarTypeFareList(carTypeFareList);

      if (!updatedEstimatedFare) {
        resetFareIfNeeded();
        setIsEstimatedFareLoading(false);
        return { res: null, list: carTypeFareList, error: null };
      }

      if (canUpdateFare) setEstimatedFare(updatedEstimatedFare);
      setIsEstimatedFareLoading(false);
      return { res: updatedEstimatedFare, list: carTypeFareList, error: null };
    },
    [setEstimatedFare, resetEstimatedFare]
  );

  return (
    <EstimatedFareContext.Provider
      value={{
        estimatedFare,
        resetEstimatedFare,
        setEstimatedFare,
        updateEstimatedFare,
        carTypeFareList,
        isEstimatedFareLoading,
      }}
    >
      {children}
    </EstimatedFareContext.Provider>
  );
}
